import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  LinearProgress,
  Paper,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import CloudDoneOutlinedIcon from "@mui/icons-material/CloudDoneOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { toast } from "react-toastify";
import {
  getPaymentPlatformBucket,
  savePaymentPlatformBucket,
  syncPaymentPlatformBucket,
  uploadPaymentPlatformBucketOutput,
} from "../api/companyApi";

const PROJECT_ID = "crediarc-prd";
const formatDate = (value) =>
  value ? new Date(value).toLocaleString() : "Not yet";
const formatBytes = (value) => {
  const bytes = Number(value || 0);
  if (!bytes) return "0 B";
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

export default function PaymentPlatformBucketPanel({
  tenantId,
  isAdmin,
  onImportQueued,
  compact = false,
}) {
  const outputInput = useRef(null);
  const [status, setStatus] = useState(null);
  const [bucketName, setBucketName] = useState("");
  const [autoSync, setAutoSync] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const loadStatus = useCallback(async () => {
    if (!tenantId) return null;
    try {
      const next = await getPaymentPlatformBucket(tenantId);
      setStatus(next);
      if (next?.bucketName) setBucketName(next.bucketName);
      setAutoSync(next?.autoSync !== false);
      return next;
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not load cloud storage");
      return null;
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const runSync = useCallback(
    async ({ quiet = false } = {}) => {
      if (!tenantId) return null;
      setSyncing(true);
      try {
        const result = await syncPaymentPlatformBucket(tenantId);
        if (result?.status === "queued") {
          if (!quiet) toast.success("New bucket data is rebuilding the MCA assessments.");
          onImportQueued?.(result.job || { id: result.importJobId, status: "queued" });
        } else if (!quiet) {
          toast.info(
            result?.status === "empty"
              ? "No CSV or Excel input files were found."
              : "Cloud input data is already up to date.",
          );
        }
        await loadStatus();
        return result;
      } catch (error) {
        if (!quiet) {
          toast.error(
            error?.response?.data?.message || "Could not refresh input data",
          );
        }
        return null;
      } finally {
        setSyncing(false);
      }
    },
    [tenantId, loadStatus, onImportQueued],
  );

  useEffect(() => {
    if (!status?.configured || status?.enabled === false || !status?.autoSync)
      return undefined;
    runSync({ quiet: true });
    const timer = window.setInterval(() => runSync({ quiet: true }), 60 * 1000);
    return () => window.clearInterval(timer);
  }, [status?.configured, status?.enabled, status?.autoSync, runSync]);

  const saveMapping = async () => {
    if (!bucketName.trim()) {
      toast.error("Enter a Google Cloud Storage bucket name.");
      return;
    }
    setSaving(true);
    try {
      await savePaymentPlatformBucket(tenantId, {
        bucketName: bucketName.trim(),
        autoSync,
        enabled: true,
        inputPrefix: "",
        outputPrefix: "output/",
      });
      toast.success("Bucket connected and access verified.");
      await loadStatus();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not connect bucket");
    } finally {
      setSaving(false);
    }
  };

  const uploadOutputs = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        await uploadPaymentPlatformBucketOutput(tenantId, file);
      }
      toast.success(
        `${files.length} output file${files.length === 1 ? "" : "s"} uploaded to cloud storage.`,
      );
      await loadStatus();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Output upload failed");
    } finally {
      setUploading(false);
    }
  };

  if (compact && !showDetails) {
    return (
      <Paper
        id="payment-platform-cloud-storage"
        variant="outlined"
        sx={{
          px: 1.75,
          py: 1.25,
          mb: 2,
          borderColor: "#dbe7f3",
          borderRadius: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <CloudDoneOutlinedIcon color="primary" fontSize="small" />
          <Typography sx={{ fontWeight: 800, color: "#0b2a4a", fontSize: 14 }}>
            Automatic cloud sync
          </Typography>
          <Chip
            size="small"
            color={status?.configured ? "success" : "default"}
            label={status?.configured ? "Connected" : "Not connected"}
          />
          <Typography sx={{ color: "#64748b", fontSize: 12.5 }}>
            {status?.lastSyncedAt
              ? `Last imported ${formatDate(status.lastSyncedAt)}`
              : "No cloud import yet"}
          </Typography>
        </Box>
        {isAdmin && (
          <Button
            size="small"
            startIcon={<SettingsOutlinedIcon />}
            onClick={() => setShowDetails(true)}
          >
            Manage
          </Button>
        )}
      </Paper>
    );
  }

  return (
    <Paper
      id="payment-platform-cloud-storage"
      variant="outlined"
      sx={{ p: 2.25, mb: 2, borderColor: "#dbe7f3", borderRadius: 3 }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CloudDoneOutlinedIcon color="primary" />
            <Typography sx={{ fontWeight: 900, color: "#0b2a4a" }}>
              Cloud data connection
            </Typography>
            <Chip
              size="small"
              color={status?.configured ? "success" : "default"}
              label={status?.configured ? "Connected" : "Not connected"}
            />
            {isAdmin && status?.autoDetected && (
              <Chip
                size="small"
                variant="outlined"
                color="primary"
                label="Matched from tenant name"
              />
            )}
          </Box>
          <Typography sx={{ color: "#64748b", fontSize: 13, mt: 0.5 }}>
            Refresh MCA inputs from a tenant bucket and publish CrediArc output
            files back to its protected <b>output/</b> folder.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {compact && (
            <Button
              variant="text"
              onClick={() => setShowDetails(false)}
            >
              Done
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            disabled={!status?.configured || syncing || loading}
            onClick={() => runSync()}
          >
            {syncing ? "Checking…" : "Refresh input data"}
          </Button>
          <Button
            variant="contained"
            startIcon={<CloudUploadOutlinedIcon />}
            disabled={!status?.configured || uploading}
            onClick={() => outputInput.current?.click()}
          >
            {uploading ? "Uploading…" : "Upload output data"}
          </Button>
          <input
            hidden
            multiple
            ref={outputInput}
            type="file"
            accept=".csv,.xls,.xlsx,.json,.jsonl,.parquet,.zip"
            onChange={uploadOutputs}
          />
        </Box>
      </Box>

      {(loading || saving || syncing || uploading) && (
        <LinearProgress sx={{ mt: 2 }} />
      )}

      {isAdmin && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "minmax(280px, 1fr) 230px auto" },
            alignItems: "center",
            gap: 1.5,
            mt: 2,
          }}
        >
          <TextField
            size="small"
            label="Tenant bucket name"
            value={bucketName}
            placeholder="client-companies-sync"
            onChange={(event) => setBucketName(event.target.value)}
            helperText={
              status?.autoDetected
                ? "Automatically matched from the tenant name. Save to pin or replace it."
                : "Bucket names are mapped per tenant."
            }
          />
          <TextField
            size="small"
            label="Google Cloud project"
            value={PROJECT_ID}
            InputProps={{ readOnly: true }}
            helperText="Fixed production project"
          />
          <Button
            variant="outlined"
            startIcon={<SaveOutlinedIcon />}
            disabled={saving || !bucketName.trim()}
            onClick={saveMapping}
            sx={{ alignSelf: "start", minHeight: 40 }}
          >
            Save & verify
          </Button>
          <Box
            sx={{
              gridColumn: { md: "1 / -1" },
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Switch
              checked={autoSync}
              onChange={(event) => setAutoSync(event.target.checked)}
              inputProps={{ "aria-label": "Automatically refresh bucket data" }}
            />
            <Typography sx={{ fontSize: 13, color: "#334155" }}>
              Automatically check for new input data while the MCA workspace is
              open
            </Typography>
          </Box>
        </Box>
      )}

      {isAdmin && status?.discoveryStatus === "ambiguous" && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          More than one bucket matches this tenant. Select and save the correct
          bucket: <b>{(status.discoveryCandidates || []).join(", ")}</b>
        </Alert>
      )}

      {isAdmin && status?.discoveryStatus === "not_found" && !status?.configured && (
        <Alert severity="info" sx={{ mt: 2 }}>
          No bucket starts with this tenant&apos;s name. Enter the bucket name
          above to connect it manually.
        </Alert>
      )}

      {status?.configured && (
        <>
          <Alert severity="info" sx={{ mt: 2 }}>
            Last checked: <b>{formatDate(status.lastCheckedAt)}</b>
            {" · "}
            Last imported: <b>{formatDate(status.lastSyncedAt)}</b>
            {" · "}
            {status.lastInputFileCount || 0} supported input file(s)
          </Alert>
          <Divider sx={{ my: 2 }} />
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 2,
            }}
          >
            <Box>
              <Typography sx={{ fontWeight: 800, color: "#0b2a4a", mb: 1 }}>
                Latest input files
              </Typography>
              {isAdmin && (status.inputFiles || []).length ? (
                status.inputFiles.slice(0, 5).map((file) => (
                  <Box
                    key={`${file.name}-${file.updated}`}
                    sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}
                  >
                    <Typography noWrap sx={{ fontSize: 13, maxWidth: "70%" }}>
                      {file.fileName}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: "#64748b" }}>
                      {formatBytes(file.size)}
                    </Typography>
                  </Box>
                ))
              ) : (
                <Typography sx={{ fontSize: 13, color: "#64748b" }}>
                  {isAdmin
                    ? "No CSV or Excel input files found."
                    : "Input file details are visible only to CrediArc administrators."}
                </Typography>
              )}
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, color: "#0b2a4a", mb: 1 }}>
                Published output files
              </Typography>
              {isAdmin && (status.outputFiles || []).length ? (
                status.outputFiles.slice(0, 5).map((file) => (
                  <Box
                    key={`${file.name}-${file.updated}`}
                    sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}
                  >
                    <Typography noWrap sx={{ fontSize: 13, maxWidth: "70%" }}>
                      {file.fileName}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: "#64748b" }}>
                      {formatBytes(file.size)}
                    </Typography>
                  </Box>
                ))
              ) : (
                <Typography sx={{ fontSize: 13, color: "#64748b" }}>
                  {isAdmin
                    ? "No CrediArc output files uploaded yet."
                    : "Output file details are visible only to CrediArc administrators."}
                </Typography>
              )}
            </Box>
          </Box>
        </>
      )}
    </Paper>
  );
}
